import { AddDeploymentRingModal } from "modules/field-mgmt/components/AddDeploymentRingModal";
import React, { useState } from "react";

import {
  confirm,
  DefaultButton,
  Heading,
  Stack,
  useFieldArray
} from "@bps/fluent-ui";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { DeploymentRing } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { AssignDeploymentRingFormValues } from "../../formTypes";
import { DeploymentRingTable } from "./DeploymentRingTable";

export interface AssignTenantDeploymentRing extends DeploymentRing {
  sortOrder: number;
}

interface DeploymentRingListProps {
  title: string;
  deploymentRings: DeploymentRing[];
  keyName: string;
}

export const DeploymentRingList: React.FC<DeploymentRingListProps> = ({
  title,
  deploymentRings,
  keyName
}) => {
  const { actions, value } = useFieldArray<AssignDeploymentRingFormValues>(
    keyName
  );

  const [showAddFieldDevice, setShowAddFieldDevice] = useState<boolean>(false);

  const selectedDeploymentRings = value.map(val => {
    const ring = deploymentRings.find(x => x.id === val.id)!;
    return {
      ...ring,
      sortOrder: val.sortOrder
    };
  });

  const onSave = async (rings: DeploymentRing[]) => {
    const nextIndex = value.length + 1;

    for (let i = 0; i < rings.length; i++) {
      actions.push({
        id: rings[i].id,
        sortOrder: nextIndex + i
      });
    }
  };

  const onRemoveItem = async (deploymentRing: AssignTenantDeploymentRing) => {
    const isConfirmed = await confirm({
      confirmButtonProps: {
        text: "Remove"
      },
      cancelButtonProps: {
        text: "Cancel"
      },
      dialogContentProps: {
        title: `Remove deployment ring ${deploymentRing.code}?`,
        subText: "Are you sure you want to remove this deployment ring?"
      }
    });
    if (isConfirmed) {
      const indexOf = value.findIndex(x => x.id === deploymentRing.id);
      if (indexOf === -1) return;

      let sortOrder = 1;

      // Reorder all
      for (let i = 0; i < value.length; i++) {
        const thisValue = value[i];
        if (thisValue.id === deploymentRing.id) continue;

        actions.update(i, {
          ...value[i],
          sortOrder
        });

        sortOrder = sortOrder + 1;
      }
      actions.remove(indexOf);
    }
  };

  const sortBySortOrder = (deploymentRings: AssignTenantDeploymentRing[]) =>
    deploymentRings.sort((a, b) => a.sortOrder - b.sortOrder);

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  return (
    <Stack verticalFill styles={{ root: { width: "50%" } }}>
      <Stack horizontal horizontalAlign="space-between">
        <Heading variant="modal-heading">{title}</Heading>

        <Stack.Item align="end">
          <DefaultButton
            disabled={!hasWritePermission}
            title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
            iconProps={{ iconName: "Add" }}
            onClick={() => {
              setShowAddFieldDevice(!showAddFieldDevice);
            }}
          >
            Add Deployment Ring
          </DefaultButton>
        </Stack.Item>
      </Stack>
      <DeploymentRingTable
        items={sortBySortOrder(selectedDeploymentRings)}
        keyName={keyName}
        onRemoveItem={onRemoveItem}
      />
      <AddDeploymentRingModal
        show={showAddFieldDevice}
        title="Add Deployment Ring"
        existingDeploymentRingIds={value?.map(x => x.id) ?? []}
        onDismiss={() => setShowAddFieldDevice(false)}
        onSave={onSave}
      />
    </Stack>
  );
};
